import type { DailyCall } from "@daily-co/daily-js"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDaily, useLocalSessionId, useParticipantIds, usePermissions } from "./callTransport"
import { meetings } from "services/api/meetingsClient"
import { cancelVideoEffectOperations } from "./videoEffects"

/**
 * Host moderation for the custom surface, matching what Daily Prebuilt's People panel offers
 * owners: mute a participant's microphone, turn their camera off, mute everyone else at once,
 * or remove them from the call. Daily enforces the permission server-side (the owner token
 * carries `canAdmin`); `canModerate` only decides whether the controls render.
 */
export type MeetingRemovalScope = {
  meetingId?: string
  roomName?: string
  /** Native bridge commands reject when media ejection fails, rather than dispatching silently. */
  onNativeEject?: (sessionId: string) => Promise<unknown>
}

async function browserRoomName(daily: DailyCall): Promise<string | undefined> {
  const room = await daily.room()
  return room && 'name' in room ? room.name : undefined
}

export function useHostModeration({ meetingId, roomName, onNativeEject }: MeetingRemovalScope = {}) {
  const daily = useDaily()
  const localSessionId = useLocalSessionId()
  const remoteIds = useParticipantIds({ filter: "remote" })
  const { canAdminParticipants } = usePermissions()
  const [removingId, setRemovingId] = useState<string>()
  const [removalError, setRemovalError] = useState('')
  const [removalNotice, setRemovalNotice] = useState('')
  const [receipt, setReceipt] = useState<{ meetingId: string; value: string }>()
  const [retryingRemoval, setRetryingRemoval] = useState(false)
  const pending = useRef(false)
  const mounted = useRef(true)
  const removalScope = useRef({ meetingId, roomName, daily })
  removalScope.current = { meetingId, roomName, daily }
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  const muteParticipant = useCallback((sessionId: string) => {
    if (!daily) return
    if (sessionId === localSessionId) {
      daily.setLocalAudio(false)
      return
    }
    daily.updateParticipant(sessionId, { setAudio: false })
  }, [daily, localSessionId])

  const stopParticipantVideo = useCallback((sessionId: string) => {
    if (!daily) return
    if (sessionId === localSessionId) {
      if (!("kind" in daily)) cancelVideoEffectOperations(daily)
      daily.setLocalVideo(false)
      return
    }
    daily.updateParticipant(sessionId, { setVideo: false })
  }, [daily, localSessionId])

  const muteAll = useCallback(() => {
    if (!daily || remoteIds.length === 0) return
    daily.updateParticipants(Object.fromEntries(remoteIds.map((id) => [id, { setAudio: false }])))
  }, [daily, remoteIds])

  const removeParticipant = useCallback(async (sessionId: string): Promise<boolean> => {
    if (!daily || sessionId === localSessionId || !canAdminParticipants || pending.current) return false
    pending.current = true
    setRemovingId(sessionId)
    setRemovalError('')
    setRemovalNotice('')
    setReceipt(undefined)
    let revoked = false
    const original = { meetingId, roomName, daily }
    const stillCurrent = () => mounted.current && removalScope.current.meetingId === original.meetingId &&
      removalScope.current.roomName === original.roomName && removalScope.current.daily === original.daily
    try {
      if (meetingId) {
        const resolvedRoom = roomName || (!("kind" in daily) ? await browserRoomName(daily) : undefined)
        if (!resolvedRoom) throw new Error('The current video room is unavailable. Reopen People and try again.')
        if (!stillCurrent()) return false
        const result = await meetings.removeMobileMeetingParticipant(meetingId, resolvedRoom, sessionId)
        if (!stillCurrent()) return false
        if (result.revoked !== true || result.scope !== 'meeting_identity') throw new Error('Removal was not confirmed. Please try again.')
        revoked = true
        if (!result.ejected) {
          if (result.removal_receipt) setReceipt({ meetingId, value: result.removal_receipt })
          setRemovalNotice('Admission was revoked, but disconnection could not be confirmed on every device.')
          if ("kind" in daily) {
            if (!onNativeEject) throw new Error('Admission was revoked, but immediate disconnection could not be confirmed. Please try again.')
            await onNativeEject(sessionId)
          } else {
            // A room switch while the server was checking presence must never eject
            // a similarly identified participant in the host's new room.
            if ((await browserRoomName(daily)) !== resolvedRoom) throw new Error('Admission was revoked. The video room changed before disconnection could be confirmed.')
            if (!stillCurrent()) return false
            await daily.updateParticipant(sessionId, { eject: true })
          }
        }
      } else {
        await daily.updateParticipant(sessionId, { eject: true })
      }
      return true
    } catch (error) {
      if (stillCurrent()) {
        const responseDetail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
        setRemovalError(typeof responseDetail === 'string' ? responseDetail : error instanceof Error ? error.message : 'Unable to remove this participant. Please try again.')
      }
      // The participant may already be gone locally. Once revoked, close the
      // original confirmation; a receipt retry handles remaining devices.
      return revoked
    } finally {
      pending.current = false
      if (mounted.current) setRemovingId(undefined)
    }
  }, [daily, localSessionId, canAdminParticipants, meetingId, roomName, onNativeEject])

  const retryRemoval = useCallback(async () => {
    if (!receipt || receipt.meetingId !== meetingId || !canAdminParticipants || pending.current) return
    pending.current = true
    setRetryingRemoval(true)
    setRemovalError('')
    const currentMeeting = receipt.meetingId
    try {
      const result = await meetings.retryMobileMeetingRemoval(currentMeeting, receipt.value)
      if (!mounted.current || removalScope.current.meetingId !== currentMeeting) return
      if (result.revoked !== true || result.scope !== 'meeting_identity') throw new Error('Disconnection was not confirmed. Please try again.')
      if (result.ejected) {
        setReceipt(undefined)
        setRemovalNotice('Disconnection was confirmed on all devices. Host readmission is still required to rejoin this meeting.')
      } else {
        setRemovalNotice('Admission was revoked, but disconnection could not be confirmed on every device.')
        setReceipt(result.removal_receipt ? { meetingId: currentMeeting, value: result.removal_receipt } : undefined)
      }
    } catch (error) {
      if (!mounted.current || removalScope.current.meetingId !== currentMeeting) return
      const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response
      if (response?.status && [400, 403, 404, 409, 410].includes(response.status)) { setReceipt(undefined); setRemovalNotice('') }
      setRemovalError(typeof response?.data?.detail === 'string' ? response.data.detail : 'Unable to confirm disconnection on every device. Please try again.')
    } finally {
      pending.current = false
      if (mounted.current) setRetryingRemoval(false)
    }
  }, [receipt, meetingId, canAdminParticipants])

  return {
    canModerate: Boolean(canAdminParticipants),
    hasRemoteParticipants: remoteIds.length > 0,
    muteParticipant,
    stopParticipantVideo,
    muteAll,
    removeParticipant,
    removingId,
    removalError,
    removalNotice,
    retryRemoval,
    retryingRemoval,
    canRetryRemoval: Boolean(receipt && receipt.meetingId === meetingId && canAdminParticipants),
    removesAllMeetingDevices: Boolean(meetingId),
  }
}
